// ---------------------------------------------------------------------------
// The browser side of THE PRIVACY CENTRE. V31.
//
// NO VOCABULARY IS SPELLED HERE OR IN THE SCREEN. Request kinds, legal-hold
// reasons, override grounds and consent purposes all arrive from
// `GET /v1/reference/privacy-vocabulary`, which reads them from the database.
// Absolute rule 4 applied to the front end, and the reason is concrete rather
// than doctrinal: a `<select>` of hold reasons written into a component goes on
// offering the old list the day somebody adds one, and the first person to find
// out is a compliance officer who cannot record the reason they actually have.
//
// NOR IS ANY RELATION NAME. The retention schedule arrives as rows. A screen
// carrying its own list of "tables holding personal data" would be a second
// register — `0088`'s argument, and it would be wrong from the first slice that
// added a table without editing it.
//
// THE SCREEN DECIDES NO DEADLINE. `days_left` and `due_at` are computed in
// `app.privacy_overview` from the statutory period the vocabulary carries. A
// browser that subtracted dates would be a second clock in a feature whose whole
// subject is whether an answer was given in time — and it would compute it in the
// reader's zone rather than the organisation's.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

/**
 * ★ `subject_name` AND NOT `worker_name` — D13.
 *
 * One of `worker_id` and `account_id` is set and the other is null, by database
 * constraint, and `subject_kind` says which without the screen having to test
 * for null. The name resolves per kind in `app.privacy_overview`: a worker's is
 * their name, an account's is its email address, and after an anonymisation that
 * reads `erased-…@erased.invalid` rather than going blank.
 */
/**
 * One edit of the person's record, offered as a candidate correction — 0136.
 *
 * The database picks these, not the screen: an event of this organisation's,
 * about the person who asked, carrying a before AND an after that differ. A read
 * writes only an `after`, so it is not one of these, and that is the mistake
 * most available to anything that lists "events about this person".
 */
export type PrivacyCorrection = {
  id: number;
  action: string;
  occurred_at: string;
  fields: string[];
};

export type PrivacyRequest = {
  id: string;
  subject_kind: string;
  worker_id: string | null;
  account_id: string | null;
  subject_name: string;
  kind: string;
  kind_label: string;
  article: string;
  /** Whether this kind of request erases the person. A property of the KIND, in
   *  the database, so the screen never spells a vocabulary id — absolute rule 4. */
  is_erasure: boolean;
  state: string;
  state_label: string;
  /** `live` | `attention` | `failed` | `neutral`, decided by the database. */
  tone: string;
  is_terminal: boolean;
  received_at: string;
  due_at: string;
  /** Whether fulfilling this kind means CHANGING the person's data rather than
   *  answering about it — Article 16 today. A property of the KIND, like
   *  `is_erasure` beside it, so the screen never spells a vocabulary id. 0136. */
  is_correction: boolean;
  /** Set only when the request was refused — 0134. */
  refusal_ground: string | null;
  refusal_label: string | null;
  /** The audit event that IS the correction this Article 16 request asked for,
   *  with what it changed — 0136. A bare row id is a citation nobody can read,
   *  so the action and the field names travel with it. Null until fulfilled. */
  rectified_by: number | null;
  rectified_action: string | null;
  rectified_at: string | null;
  rectified_fields: string[] | null;
  /** The edits to this person's record this organisation has actually made, and
   *  ONLY while a correcting request is still open — the list the screen has to
   *  pick from, because the database will not accept a fulfilment without one.
   *  Null on every other request rather than an empty array: nothing is being
   *  offered, which is not the same as nothing being available. 0136. */
  corrections: PrivacyCorrection[] | null;
  /** When it reached a terminal state. Null while it is still open — 0134.
   *  Declared here because `app.privacy_overview` has sent it since 0134 and
   *  this type did not say so; annotating PRIVACY_REQUESTS with it is what
   *  asked the question. */
  closed_at: string | null;
  days_left: number;
  /** Whether the person asked for this themselves, which is the ordinary case. */
  opened_by_worker: boolean;
  live_holds: number;
  overrides: number;
};

export type LegalHold = {
  id: string;
  subject_kind: string;
  worker_id: string | null;
  account_id: string | null;
  subject_name: string;
  reason: string;
  reason_label: string;
  /** A case number. There is no note field on a hold, at any layer. */
  reference: string;
  opened_at: string;
  released_at: string | null;
  is_live: boolean;
};

export type ConsentEntry = {
  id: string;
  worker_id: string;
  worker_name: string;
  purpose: string;
  purpose_label: string;
  article: string;
  is_granted: boolean;
  decided_at: string;
  decided_by_worker: boolean;
  /** How many times this decision has been changed. Never when, never which way. */
  changes: number;
};

export type ErasureEvent = {
  id: string;
  subject_kind: string;
  worker_id: string | null;
  account_id: string | null;
  performed_at: string;
  relations_walked: number;
  /** Three-valued. TRUE the account was closed and unnamed; FALSE this
   *  organisation's processing went and the account stands; NULL there was no
   *  account to reach. A severance shown as an erasure is the lie the whole
   *  register exists to prevent. */
  account_anonymised: boolean | null;
  account_retained_reason: string | null;
  account_retained_label: string | null;
  overrides_used: number;
  /** What the erasure was REFUSED, because the rows are append-only evidence —
   *  0130. Rendered alongside `rows_erased` and never instead of it: a screen
   *  showing only what went reads as "everything went". */
  rows_retained: number;
  retained_relations: { relation: string; rows: number }[];
  overrides: {
    ground: string;
    ground_label: string;
    authorised_by: string | null;
    authorised_at: string;
    hold_reason: string;
    hold_reference: string;
  }[];
  rows_erased: number;
};

export type RetentionRow = {
  relation: string;
  anchor_column: string | null;
  retained_with: string | null;
  retention_days: number;
  action: string;
  action_label: string;
  basis: string;
};

export type WorkingTimeRow = {
  country: string;
  max_daily_hours: string;
  max_weekly_hours: string;
  reference_period_days: number;
  min_daily_rest_hours: string;
  min_weekly_rest_hours: string;
  max_consecutive_days: number;
  source: string;
  is_ours: boolean;
};

/** The accounts an account-kind request may be opened about. `app.privacy_people`,
 *  which rides on this read because it needs `privacy.manage` — 0127. */
export type PrivacyPerson = {
  account_id: string;
  email: string;
  membership: string;
  role: string;
  status: string;
};

export type SubjectKind = {
  id: string;
  label: string;
  description: string;
  erasure_action: string;
  erasure_action_label: string;
};

export type PrivacyOverview = {
  subject_kinds: SubjectKind[];
  people: PrivacyPerson[];
  account_walk: { relation: string; attname: string; scope: string }[];
  requests: PrivacyRequest[];
  holds: LegalHold[];
  consent: ConsentEntry[];
  erasures: ErasureEvent[];
  retention: RetentionRow[];
  coverage_defects: { relation: string; defect: string }[];
  working_time: WorkingTimeRow[];
};

export type PrivacyVocabulary = {
  /** Why a request was refused, from the database — Article 12(4). 0134. */
  refusal_grounds: { id: string; label: string; description: string; article: string }[];
  request_kinds: { id: string; label: string; article: string; statutory_days: number }[];
  request_states: { id: string; label: string; is_terminal: boolean; tone: string }[];
  hold_reasons: { id: string; label: string; description: string }[];
  override_grounds: { id: string; label: string; description: string }[];
  consent_purposes: { id: string; label: string; description: string; article: string }[];
};

// EVERY WRITE GOES THROUGH `mutate`, WHICH IS ALREADY HERE. It carries the CSRF
// header and puts every value in the body rather than the URL — V4's lesson, and
// a hand-rolled `fetch` here would be a second place that has to remember both.
const post = (base: string, path: string, body?: unknown) => mutate("POST", base, path, body ?? {});

const subjectBody = (subject: { kind: string; id: string }, rest: Record<string, unknown>) => ({
  ...rest,
  ...(subject.kind === "worker" ? { worker_id: subject.id } : { account_id: subject.id }),
});

export const privacy = {
  overview: (base: string) => read(base, "/v1/privacy/overview"),
  vocabulary: (base: string) => read(base, "/v1/reference/privacy-vocabulary"),
  exportWorker: (base: string, workerId: string) =>
    read(base, `/v1/privacy/workers/${workerId}/export`),
  exportAccount: (base: string, accountId: string) =>
    read(base, `/v1/privacy/accounts/${accountId}/export`),
  // ONE BODY KEY PER SUBJECT, never both — the Worker refuses a body naming two
  // people, so these helpers cannot construct one.
  openRequest: (base: string, subject: { kind: string; id: string }, kind: string) =>
    post(base, "/v1/privacy/requests", subjectBody(subject, { kind })),
  openHold: (
    base: string,
    subject: { kind: string; id: string },
    reason: string,
    reference: string,
  ) => post(base, "/v1/privacy/holds", subjectBody(subject, { reason, reference })),
  releaseHold: (base: string, holdId: string) =>
    post(base, `/v1/privacy/holds/${holdId}/release`, {}),
  authoriseOverride: (base: string, requestId: string, holdId: string, ground: string) =>
    post(base, "/v1/privacy/overrides", { request_id: requestId, hold_id: holdId, ground }),
  // ONE CALL FOR ALL THREE TERMINAL STATES. The state travels in the body
  // because it is a vocabulary value, and a path per outcome would be a second
  // list that has to agree with app.subject_request_state. Since 0136 the same
  // call also carries the CORRECTION a rectification was fulfilled against.
  //
  // BOTH EXTRAS ARE OMITTED RATHER THAN SENT AS NULL. They are omitted
  // rather than sent as null: `app.close_subject_request` refuses a ground on
  // anything but a refusal and a correction on anything but a correcting
  // fulfilment, so sending either unasked turns a valid closure into a refusal.
  closeRequest: (
    base: string,
    requestId: string,
    state: string,
    refusalGround?: string,
    rectifiedBy?: number,
  ) =>
    post(base, `/v1/privacy/requests/${requestId}/close`, {
      state,
      ...(refusalGround ? { refusal_ground: refusalGround } : {}),
      ...(rectifiedBy !== undefined ? { rectified_by: rectifiedBy } : {}),
    }),
  erase: (base: string, requestId: string) =>
    post(base, `/v1/privacy/requests/${requestId}/erasure`, {}),
  recordConsent: (base: string, workerId: string, purpose: string, granted: boolean) =>
    post(base, "/v1/privacy/consent", { worker_id: workerId, purpose, granted }),
};
